import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { AlertTriangle, Clock, CheckCircle2, MoonStar, Download, FileText } from 'lucide-react';
import { useLetterheadExport } from './useLetterheadExport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  pm: 'Preventative Maintenance',
  calibration: 'Calibration',
  inspection: 'Inspection',
  service: 'Service',
  emergency: 'Emergency Service',
};

function StatusPill({ row }) {
  if (row.snoozed) return <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold border bg-slate-100 border-slate-300 text-slate-700">SNOOZED · {row.snoozed_until}</span>;
  if (row.status === 'overdue') return <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold border bg-red-50 border-red-300 text-red-800">OVERDUE</span>;
  if (row.status === 'upcoming') return <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold border bg-amber-50 border-amber-300 text-amber-800">UPCOMING</span>;
  return <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold border bg-green-50 border-green-300 text-green-800">OK</span>;
}

export default function ComplianceReport() {
  const { requestExport, exportFeedback, exporting } = useLetterheadExport('compliance-letterhead-export');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reports/compliance`);
      setData(res.data);
    } catch {
      toast.error('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doSnooze = async (row, days) => {
    try {
      await axios.post(`${API}/equipment/${row.equipment_id}/maintenance-schedules/${row.schedule_id}/snooze`, { days });
      toast.success(`Snoozed for ${days} days`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Snooze failed');
    }
  };

  const doUnsnooze = async (row) => {
    try {
      await axios.post(`${API}/equipment/${row.equipment_id}/maintenance-schedules/${row.schedule_id}/unsnooze`);
      toast.success('Un-snoozed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Un-snooze failed');
    }
  };

  const downloadExport = async (format) => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/compliance/export?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (format === 'pdf') throw error;
      toast.error('Export failed');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="text-slate-500 py-6">Loading compliance report...</div>;
  if (!data) return null;

  const { counts, rows, as_of, upcoming_days } = data;

  return (
    <div className="space-y-4" data-testid="compliance-report">
      {exportFeedback}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Overdue" value={counts.overdue} icon={AlertTriangle} tone="red" testid="compliance-overdue" />
        <SummaryCard label={`Upcoming (${upcoming_days}d)`} value={counts.upcoming} icon={Clock} tone="amber" testid="compliance-upcoming" />
        <SummaryCard label="Snoozed" value={counts.snoozed} icon={MoonStar} tone="slate" testid="compliance-snoozed" />
        <SummaryCard label="OK" value={counts.ok} icon={CheckCircle2} tone="green" testid="compliance-ok" />
      </div>

      <Card className="p-4 border border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
          <div>
            <div className="text-sm text-slate-600">As of {as_of} · {counts.total_schedules} schedules across {counts.total_equipment} equipment</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadExport('csv')} disabled={downloading} data-testid="compliance-export-csv">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button size="sm" onClick={() => requestExport(() => downloadExport('pdf'))} disabled={downloading || exporting} className="bg-slate-900 hover:bg-slate-800" data-testid="compliance-export-pdf">
              <FileText size={14} className="mr-1" /> PDF
            </Button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500 border border-dashed border-slate-300 rounded-md">
            No maintenance schedules defined yet. Add schedules from any equipment&apos;s detail page.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compliance-table">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left px-3 py-2">Equipment</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Interval</th>
                  <th className="text-left px-3 py-2">Last Done</th>
                  <th className="text-left px-3 py-2">Next Due</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={`${r.equipment_id}-${r.schedule_id}`} data-testid={`compliance-row-${r.schedule_id}`}>
                    <td className="px-3 py-2">
                      <button className="text-slate-900 hover:text-blue-600 hover:underline text-left" onClick={() => navigate(`/equipment/${r.equipment_id}`)} data-testid={`goto-eq-${r.equipment_id}`}>
                        {r.equipment_name}
                      </button>
                      {r.billable === false && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold border border-slate-300 bg-slate-100 text-slate-700">NON-BILLABLE</span>}
                    </td>
                    <td className="px-3 py-2">{TYPE_LABELS[r.type] || r.type}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {r.interval_days ? `${r.interval_days}d` : null}
                      {r.interval_days && r.interval_hours ? ' · ' : null}
                      {r.interval_hours ? `${r.interval_hours}h` : null}
                      {(r.interval_days || r.interval_hours) && r.interval_uses ? ' · ' : null}
                      {r.interval_uses ? `${r.interval_uses}u` : null}
                      {!r.interval_days && !r.interval_hours && !r.interval_uses ? '—' : null}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {r.last_performed_at || '—'}
                      {r.last_performed_hours != null && <span className="text-slate-400"> @{r.last_performed_hours}h</span>}
                      {r.last_performed_uses != null && <span className="text-slate-400"> @{r.last_performed_uses}u</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-800 text-xs">
                      {r.next_due_at || '—'}
                      {r.next_due_hours != null && <span className="text-slate-500"> @{r.next_due_hours}h</span>}
                      {r.next_due_uses != null && <span className="text-slate-500"> @{r.next_due_uses}u</span>}
                    </td>
                    <td className="px-3 py-2"><StatusPill row={r} /></td>
                    <td className="px-3 py-2">
                      {r.status === 'overdue' && !r.snoozed && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`snooze-btn-${r.schedule_id}`}>
                              <MoonStar size={12} className="mr-1" /> Snooze
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-40 p-2">
                            <div className="flex flex-col gap-1">
                              <Button size="sm" variant="ghost" className="justify-start" onClick={() => doSnooze(r, 7)} data-testid={`snooze-7-${r.schedule_id}`}>7 days</Button>
                              <Button size="sm" variant="ghost" className="justify-start" onClick={() => doSnooze(r, 14)} data-testid={`snooze-14-${r.schedule_id}`}>14 days</Button>
                              <Button size="sm" variant="ghost" className="justify-start" onClick={() => doSnooze(r, 30)} data-testid={`snooze-30-${r.schedule_id}`}>30 days</Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      {r.snoozed && (
                        <Button size="sm" variant="outline" onClick={() => doUnsnooze(r)} data-testid={`unsnooze-btn-${r.schedule_id}`}>Un-snooze</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone, testid }) {
  const toneClass = {
    red: 'bg-red-50 border-red-200 text-red-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    green: 'bg-green-50 border-green-200 text-green-800',
  }[tone] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`rounded-md border p-4 ${toneClass}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide font-semibold">{label}</div>
        <Icon size={18} />
      </div>
      <div className="text-3xl font-bold mt-2 font-mono">{value}</div>
    </div>
  );
}
